import React, { useEffect, useState } from "react";
import { SketchPicker } from "react-color";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
} from "@mui/material";
import axios from "axios";
import { useFormik } from "formik";
import * as yup from "yup";

import useSnackbarAlert from "../../utils/snackbarHandlers";
import CommonButton from "../common/button/Button";
import CommonDropDown from "../common/dropdown/CommonDropdown";
import CommonInput from "../common/input/InputBox";
import UploadImage from "../common/uploadImage/UploadImage";
import "./settings.css";

interface Setting {
  settingId: string;
  screenNumber: string;
  headerFooterStyle: string;
  ticketsCount: string;
  otherCount?: string;
  rows: string;
  column: string;
  emptyTicketsDisplay: string;
  backgroundType: string;
  backgroundValue: any;
}

type BodyType = {
  userId: any;
  screenNumber: string;
  headerFooterStyle: string;
  ticketsCount: any;
  rows: string;
  column: string;
  emptyTicketsDisplay: any;
  lastUpdatedBy: string;
  settingId?: any; // Add 'settingId' as an optional property
  backgroundType: string;
  backgroundValue: any;
};

const settingIntialData = {
  settingId: "",
  screenNumber: "",
  headerFooterStyle: "",
  ticketsCount: "",
  otherCount: "",
  rows: "",
  column: "",
  emptyTicketsDisplay: "",
  backgroundType: "COLOR",
  backgroundValue: "",
};

//will mmove this to config file later
const options: any = [
  { value: "16", key: "16" },
  { value: "24", key: "24" },
  { value: "32", key: "32" },
  { value: "40", key: "40" },
  { value: "48", key: "48" },
  { value: "56", key: "56" },
  { value: "64", key: "64" },
  { value: "72", key: "72" },
  { value: "80", key: "80" },
  { value: "88", key: "88" },
  { value: "96", key: "96" },
  { value: "Other", key: "Other" },
];

const backgroundTypeOptions: any = [
  { value: "COLOR", key: "Color" },
  { value: "IMAGE", key: "Image" },
];

const themeOptions: any = [
  { value: "", key: "Select Theme" },
  { value: "SNOWFLAKES", key: "Snow flakes" },
];

const screenArray = [1, 2];

const SettingPage: React.FC = () => {
  //@ts-ignore
  const config = window.config;
  const apiBaseURL = config?.server || "http://18.217.95.31:8080";

  const subscription = localStorage.getItem("subscription");
  const subscriptionDetails = subscription? JSON.parse(localStorage.getItem("subscription") || ""): null;
  const { t } = useTranslation("translation");
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState<boolean>(false);
  const [settingDetails, setSettingDetails] = useState<Setting>(settingIntialData);
  const [colorOption1, setColorOption1] = useState<string>("#FF0000");
  const [colorOption2, setColorOption2] = useState<string>("#FCFF00");
  const [colorOption3, setColorOption3] = useState<string>("#0EECFF");
  const [image, setImage] = React.useState("");
  const [imageLoading, setImageLoading] = useState(false);
  const { showErrorMessage, showSuccessMessage } = useSnackbarAlert();
  const userData = localStorage.getItem("UserData");
  const userDetails = userData ? JSON.parse(localStorage.getItem("UserData") || "") : null;
  const settingsData = localStorage.getItem("settings");
  const settingsDetail = settingsData ? JSON.parse(localStorage.getItem("settings") || "") : null;
  const screen = localStorage.getItem("screenNumber");
  const screenNumberDetails = screen
    ? JSON.parse(localStorage.getItem("screenNumber") || "")
    : null;

  const [screenNumber, setScreenNumber] = React.useState(
    screenNumberDetails ? screenNumberDetails : 1,
  );

  const { id } = useParams();

  const validationSchema = yup.object().shape({
    headerFooterStyle: yup.string().required(t("headerFooterStyleRequired")),
    ticketsCount: yup.string().trim(),
    rows: yup.string().required(t("rowsRequired")),
    column: yup.string().required(t("columnRequired")),
    emptyTicketsDisplay: yup.string().required(t("emptyTicketsDisplayRequired")),
    // backgroundValue: yup.mixed().required(t("emptyBackgroundValue")),
  });

  useEffect(() => {
    if (id) {
      getSettingsDetails(id);
    } else {
      getSettingsDetails(userDetails?.uId);
    }
  }, [screenNumber]);

  const getSettingsDetails = (uId: any) => {
    axios
      .get(`${apiBaseURL}/api/settings/fetchSettings?id=${uId}&screenNumber=${screenNumber}`)
      .then((data: any) => {
        if (data?.status == 200) {
          let settingsDetail = data?.data?.settings;
          if(settingsDetail?.backgroundValue && settingsDetail?.backgroundType === "COLOR") {
            const splittedValues = settingsDetail?.backgroundValue?.split(',');
            console.log("Splitted values - ", splittedValues);
            setColorOption1(splittedValues[0]);
            setColorOption2(splittedValues[1]);
            setColorOption3(splittedValues[2]);
          }

          if(settingDetails?.backgroundType === "IMAGE") {
            setImage(settingsDetail.backgroundValue);
          }

          setSettingDetails({
            settingId: settingsDetail?.id,
            screenNumber: settingsDetail.screenNumber,
            headerFooterStyle: settingsDetail?.headerFooterStyle,
            ticketsCount: settingsDetail?.ticketsCount,
            rows: settingsDetail?.rows,
            column: settingsDetail?.column,
            emptyTicketsDisplay: settingsDetail?.emptyTicketsDisplay,
            backgroundType: settingsDetail?.backgroundType,
            backgroundValue: settingsDetail?.backgroundValue,
          });
          let settings = {
            settingId: settingsDetail?.id,
            screenNumber: settingsDetail?.screenNumber,
            headerFooterStyle: settingsDetail?.headerFooterStyle,
            ticketsCount: settingsDetail?.ticketsCount,
            rows: settingsDetail?.rows,
            column: settingsDetail?.column,
            emptyTicketsDisplay: settingsDetail?.emptyTicketsDisplay,
            backgroundType: settingsDetail?.backgroundType,
            backgroundValue: settingsDetail?.backgroundValue,
          };
          localStorage.setItem("settings", JSON.stringify(settings));
        }
      })
      .catch((err: any) => {
        console.log("err", err);
      })
      .finally(() => {});
  };

  const handleImageUpload = (event: any) => {
    const file = URL.createObjectURL(event?.target?.files[0]);
    setImage(file);
    const fileFound = event.target.type === "file" && event.target.files[0];
    console.log("File found - ", fileFound);
    setFieldValue("backgroundValue", fileFound);
  };

  const onHandleSubmit = (value: any) => {
    if(values?.backgroundType !== "COLOR" && values?.backgroundValue === "") {
      setErrors({backgroundValue: t("emptyBackgroundValue")});
      return;
    }
    setLoading(true);
    const customBackgroundValue = `${colorOption1},${colorOption2},${colorOption3}`;
    console.log("Condition check - ", value?.backgroundType === "COLOR", customBackgroundValue, (value?.backgroudType === 'COLOR'));
    let finalizedBackgroundValue = value.backgroundValue;
    if(value?.backgroundType === "COLOR") {
      console.log("Inside if statement");
      finalizedBackgroundValue= customBackgroundValue;
    };

    let body: BodyType = {
      userId: userDetails?.uId,
      screenNumber: screenNumber,
      headerFooterStyle: value?.headerFooterStyle,
      ticketsCount: value?.ticketsCount === "Other" ? value?.otherCount : value?.ticketsCount,
      rows: value?.rows,
      column: value?.column,
      emptyTicketsDisplay: value?.emptyTicketsDisplay,
      lastUpdatedBy: userDetails?.fullName,
      backgroundType: value?.backgroundType,
      backgroundValue: finalizedBackgroundValue,
    };

    console.log("Body - ", body);
    if (id) {
      // For Admin - customizing settings of different shopowner
      body.userId = id;
      getSettingsDetails(body?.userId);
    }
    const fileHeaders = {
      accept: "application/json",
      "Accept-Language": "en-US,en;q=0.8",
      "Content-Type": "multipart/form-data",
    };

    if (settingsDetail?.settingId) {
      // For Update
      let id = settingsDetail?.settingId;

      axios
        .put(`${apiBaseURL}/api/settings/updateSettings?id=${id}`, body, {
          headers: fileHeaders,
        })
        .then((data: any) => {
          showSuccessMessage(t("settingHasBeenUpdated", "Setting has been updated"));
          body["settingId"] = id;
          localStorage.setItem("settings", JSON.stringify(body));
          if (userDetails?.role === "ADMIN") {
            navigate("/admin/shopList");
          } else if (userDetails?.role === "SHOPOWNER") {
            navigate("/shopOwner/dashboard");
          }
        })
        .catch((err: any) => {
          console.log("err", err);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // For Save
      axios
        .post(`${apiBaseURL}/api/settings/saveSettings`, body, {
          headers: fileHeaders,
        })
        .then((data: any) => {
          showSuccessMessage(data?.data?.message);
          localStorage.setItem("settings", JSON.stringify(body));
          if (userDetails?.role === "ADMIN") {
            navigate("/admin/dashboard");
          } else if (userDetails?.role === "SHOPOWNER") {
            navigate("/shopOwner/dashboard");
          }
        })
        .catch((err: any) => {
          console.log("err", err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const formik = useFormik({
    initialValues: {
      headerFooterStyle: settingDetails?.headerFooterStyle ? settingDetails?.headerFooterStyle : "",
      ticketsCount: settingDetails?.ticketsCount
        ? options.some((obj: any) => obj.value === settingDetails?.ticketsCount?.toString())
          ? settingDetails?.ticketsCount
          : "Other"
        : 56,
      otherCount: settingDetails?.ticketsCount
        ? options.some((obj: any) => obj.value === settingDetails?.ticketsCount?.toString())
          ? ""
          : settingDetails?.ticketsCount
        : "",
      emptyTicketsDisplay: settingDetails?.emptyTicketsDisplay
        ? settingDetails?.emptyTicketsDisplay
        : "",
      rows: settingsDetail?.rows ? settingsDetail?.rows : "",
      column: settingsDetail?.column ? settingsDetail?.column : "",
      backgroundType: settingDetails?.backgroundType ?? "COLOR",
      backgroundValue: settingDetails?.backgroundValue ? settingDetails?.backgroundValue : "",
    },
    onSubmit: onHandleSubmit,
    enableReinitialize: true,
    validationSchema: validationSchema,
  });

  const { values, errors, handleSubmit, touched, handleChange, setFieldValue, setErrors } = formik;

  console.log("Values - ", values);

  const onLoad = () => {
    setImageLoading(false);
  };

  return (
    <div className='settings-page'>
      <div className='settings-page__screen-buttons'>
        {screenArray.map((item, index) => {
          return (
            <CommonButton
              key={index}
              id='login-button'
              className={
                item == screenNumber
                  ? "settings-screen-selected-button"
                  : "settings-screen-button"
              }
              variant={index == screenNumber ? "outlined" : "contained"}
              onClick={() => {
                setScreenNumber(index + 1);
              }}
              ignoreButtonRootClass={true}
            >
              {t("screen", { screenCount: index + 1 })}
            </CommonButton>
          );
        })}
      </div>
      <Grid className='settings-page__title'>{t("globalSettings")}</Grid>
      <Grid className='settings-page__subtitle'>{t("pleaseFillOutInformation")}</Grid>
      <Grid className='settings-page__label'>{t("selectHeaderFooterInformation")}</Grid>
      <Grid>
        <RadioGroup
          aria-labelledby='demo-radio-buttons-group-label'
          value={values?.headerFooterStyle}
          onChange={handleChange}
          name='headerFooterStyle'
        >
          {/* {subscriptionDetails.customSubscriptionId !== 1 && (
            <>
              <FormControlLabel
                value='ADVERTISEMENT_HEADER'
                control={<Radio />}
                label={t("advertisementHeader")}
              />
              <FormControlLabel
                value='ADVERTISEMENT_FOOTER'
                control={<Radio />}
                label={t("advertisementFooter")}
              />
            </>
          )} */}
          <FormControlLabel
            value='LOTTERY_UPDATE_HEADER'
            control={<Radio />}
            label={t("lotteryUpdateHeader")}
          />
          <FormControlLabel
            value='LOTTERY_UPDATE_FOOTER'
            control={<Radio />}
            label={t("lotteryUpdateFooter")}
          />
          <FormControlLabel
            value='NO_HEADER_FOOTER'
            control={<Radio />}
            label={t("noHeaderFooter")}
          />
        </RadioGroup>
      </Grid>
      <Grid>
        <Grid className='settings-page__label'>{t("lottryTicketToShow")}</Grid>
        <Grid className='settings-page__form-row settings-page__form-row--tickets'>
          <Grid className='settings-page__field settings-page__field--tickets'>
            <CommonDropDown
              value={values?.ticketsCount}
              label={t("ticketsCount")}
              name='ticketsCount'
              onChange={handleChange}
              options={options}
              sx={{ width: "100%", height: "10%", padding: "2px" }}
            />
          </Grid>

          {values?.ticketsCount === "Other" && (
            <Grid className='settings-page__field'>
              <CommonInput
                id='ticketsCount-id-input-box'
                type='number'
                required
                label={t("otherValue")}
                name='otherCount'
                value={values.otherCount}
                placeholder={t("enterTicketCount")}
                onChange={handleChange}
                sx={true}
                className='input-box'
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid>
        <Grid className='settings-page__label'>{t("WhatToShowWithhoutLottery")}</Grid>
        <Grid>
          <RadioGroup
            aria-labelledby='demo-radio-buttons-group-label'
            defaultValue='empty'
            name='emptyTicketsDisplay'
            value={values?.emptyTicketsDisplay}
            onChange={handleChange}
          >
            <FormControlLabel value='EMPTY' control={<Radio />} label={t("empty")} />
            <FormControlLabel value='COMING_SOON' control={<Radio />} label={t("comingSoon")} />
            <FormControlLabel value='SOLD_OUT' control={<Radio />} label={t("soldOut")} />
          </RadioGroup>
        </Grid>
      </Grid>
      <Grid>
        <Grid className='settings-page__label' spacing={3}>
          {t("lotteryTicketsInRowsAndColumns")}
        </Grid>
        <Grid className='settings-page__form-row settings-page__form-row--rows-cols'>
          <Grid className='settings-page__field settings-page__field--rows-cols'>
            <CommonInput
              id='title-id-input-box'
              name='rows'
              type='number'
              label={t("rows")}
              error={!!errors?.rows}
              helperText={errors.rows as any}
              placeholder={t("enterRows")}
              onChange={handleChange}
              value={values?.rows}
              className='input-box'
              sx={true}
            />
          </Grid>
          <Grid className='settings-page__field settings-page__field--rows-cols'>
            <CommonInput
              className='input-box'
              id='title-id-input-box'
              name='column'
              type='number'
              error={!!errors?.column}
              helperText={errors.column as any}
              label={t("column")}
              placeholder={t("enterColumn")}
              onChange={handleChange}
              value={values?.column}
              sx={true}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Admin specific Grid */}
      {(userDetails?.role === "ADMIN" || userDetails?.role === "SHOPOWNER") && (
        <Grid>
          <Grid className='settings-page__label' spacing={3}>
            {t("commonModuleGridSettingPageTitle")}
          </Grid>
          <Grid className='settings-page__color-section'>
            <Grid className='settings-page__field settings-page__field--background-type'>
              <CommonDropDown
                value={values?.backgroundType}
                label={t("backgroundType")}
                name='backgroundType'
                onChange={(e) => {
                  handleChange(e);
                  setImage("");
                  setFieldValue("backgroundValue", "");
                }}
                options={[...backgroundTypeOptions, { value: "THEME", key: "Theme" }]}
                sx={{ width: "100%", height: "10%", padding: "2px" }}
              />
            </Grid>

            {values.backgroundType === "THEME" && (
              <Grid className='settings-page__field settings-page__field--background-type'>
                <CommonDropDown
                  value={values?.backgroundValue || ""}
                  label={t("themeName")}
                  name='backgroundValue'
                  onChange={handleChange}
                  options={themeOptions}
                  sx={{ width: "100%", height: "10%", padding: "2px" }}
                  error={errors.backgroundValue ? true : false}
                  helperText={
                    (errors?.backgroundValue as string) && (errors.backgroundValue as string)
                  }
                />
              </Grid>
            )}

            {values.backgroundType === "COLOR" && (
              <Grid className='settings-page__color-pickers'>
                <SketchPicker
                  onChange={(color) => {
                    setFieldValue("backgroundValue", color.hex);
                    setColorOption1(color.hex);
                  }}
                  color={colorOption1}
                  className="settings-page__sketch-picker"
                />
                <SketchPicker
                  onChange={(color) => {
                    setColorOption2(color.hex);
                  }}
                  color={colorOption2}
                  className="settings-page__sketch-picker"
                />
                <SketchPicker
                  onChange={(color) => {
                    setColorOption3(color.hex);
                  }}
                  color={colorOption3}
                  className="settings-page__sketch-picker"
                />
              </Grid>
            )}

            {values.backgroundType === "IMAGE" && (
              <Grid className='relative' display={"flex"} justifyContent={"center"}>
                <input
                  id='settingsUploadImage'
                  type='file'
                  className='hover:cursor-pointer'
                  name={"backgroundValue"}
                  accept={".png,.jpg,.jpeg"}
                  onChange={(event: any) => handleImageUpload(event)}
                  onClick={(e: any) => {
                    e.target.value = null;
                  }}
                />
              </Grid>
            )}
          </Grid>

          {image && (
            <Grid className='settings-page__preview-image'>
              <img
                alt='lottery'
                src={image}
                onLoad={onLoad}
                className='w-auto h-full aspect-square object-cover rounded-[20%] mt-2'
              />

              {imageLoading && (
                <CircularProgress
                  color='inherit'
                  size={14}
                  thickness={6}
                  style={{ marginLeft: "10%" }}
                />
              )}
            </Grid>
          )}
        </Grid>
      )}
      {/* End of admin specific grid */}

      <div className='settings-page__save-wrap'>
        <CommonButton
          id='login-button'
          className='setting-button'
          variant='contained'
          disabled={values?.ticketsCount === "Other" && !values.otherCount ? true : false}
          onClick={() => {
            handleSubmit();
          }}
        >
          {t("saveSettings")}
          {loading && (
            <CircularProgress
              color='inherit'
              size={14}
              thickness={6}
              style={{ marginLeft: "8px" }}
            />
          )}
        </CommonButton>
      </div>
    </div>
  );
};

export default SettingPage;
